/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Tabs } from "antd";
import { toast } from "react-toastify";
import { all_routes } from "../../../router/all_routes";
import {
  PaystubItem,
  YEAR_OPTIONS,
  MONTH_OPTIONS,
  findAllPaystubs,
  syncPaystubs,
  fetchEnglishPdf,
  fetchHindiPdf,
  openBase64Pdf,
  downloadBase64Pdf,
} from "./salaryStatement.service";

type LanguageTab = "english" | "hindi";

interface DownloadedFileItem {
  id: string;
  fileName: string;
  label: string;
  downloadedAt: number;
  sequenceNumber: string;
  language: LanguageTab;
  base64: string;
}

const DOWNLOADS_KEY = "salary_statement_downloads";

const HINDI_MONTH_LABELS: Record<string, string> = {
  January: "जनवरी",
  February: "फ़रवरी",
  March: "मार्च",
  April: "अप्रैल",
  May: "मई",
  June: "जून",
  July: "जुलाई",
  August: "अगस्त",
  September: "सितंबर",
  October: "अक्टूबर",
  November: "नवंबर",
  December: "दिसंबर",
};

const PAYROLL_TYPE_HINDI: Record<string, string> = {
  "Bonus payment": "बोनस भुगतान",
  "Regular payroll run": "नियमित पेरोल रन",
  "Salary Payment": "वेतन भुगतान",
  "Off-cycle payment": "ऑफ-साइकिल भुगतान",
  Arrears: "बकाया भुगतान",
  "Final Settlement": "अंतिम निपटान",
  "Advance Payment": "अग्रिम भुगतान",
  Incentive: "प्रोत्साहन राशि",
  Reimbursement: "प्रतिपूर्ति",
  Encashment: "नकदीकरण",
};

function getTranslatedType(type?: string, isHindi?: boolean): string {
  if (!type) return "-";
  if (!isHindi) return type;
  if (PAYROLL_TYPE_HINDI[type]) return PAYROLL_TYPE_HINDI[type];
  const lower = type.toLowerCase();
  for (const [k, v] of Object.entries(PAYROLL_TYPE_HINDI)) {
    if (k.toLowerCase() === lower) return v;
  }
  if (lower.includes("bonus")) return "बोनस भुगतान";
  if (lower.includes("regular") || lower.includes("payroll")) return "नियमित पेरोल रन";
  if (lower.includes("salary")) return "वेतन भुगतान";
  if (lower.includes("arrear")) return "बकाया भुगतान";
  return type;
}

/** Read the logged-in employee id from localStorage (same pattern as other pages). */
function getLoggedInEmpId(): string {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return String(user?.empId || user?.details?.empId || user?.usrid || "");
  } catch {
    return "";
  }
}

function loadDownloads(): DownloadedFileItem[] {
  try {
    const raw = localStorage.getItem(DOWNLOADS_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

function persistDownloads(list: DownloadedFileItem[]): void {
  try {
    localStorage.setItem(DOWNLOADS_KEY, JSON.stringify(list));
  } catch {
    /* quota exceeded — keep in-memory only */
  }
}

const SalaryStatement: React.FC = () => {
  const empId = useMemo(() => getLoggedInEmpId(), []);

  const [activeTab, setActiveTab] = useState<LanguageTab>("english");
  const [selectedYear, setSelectedYear] = useState<string>("2025");
  const [selectedMonthName, setSelectedMonthName] = useState<string>("March");

  const [paystubs, setPaystubs] = useState<PaystubItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [syncing, setSyncing] = useState<boolean>(false);
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);

  const [downloadedFiles, setDownloadedFiles] = useState<DownloadedFileItem[]>(
    () => loadDownloads()
  );
  const [showDownloadsModal, setShowDownloadsModal] = useState<boolean>(false);
  const [selectedSeq, setSelectedSeq] = useState<string | null>(null);

  const monthNumber = MONTH_OPTIONS.indexOf(selectedMonthName) + 1;

  const fetchPaystubs = useCallback(async () => {
    const year = parseInt(selectedYear, 10);
    const month = MONTH_OPTIONS.indexOf(selectedMonthName) + 1;
    if (!year || month < 1) return;
    setLoading(true);
    try {
      const list = await findAllPaystubs({ year, month, empId });
      setPaystubs(list);
    } catch (err: any) {
      setPaystubs([]);
      toast.error(err?.message || "Failed to load salary statement");
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedMonthName, empId]);

  useEffect(() => {
    fetchPaystubs();
  }, [fetchPaystubs]);

  const handleSync = useCallback(async () => {
    if (!empId) {
      toast.error("Employee ID not found");
      return;
    }
    setSyncing(true);
    try {
      await syncPaystubs(empId);
      toast.success("Sync completed");
      await fetchPaystubs();
    } catch (err: any) {
      toast.error(err?.message || "Sync failed");
    } finally {
      setSyncing(false);
    }
  }, [empId, fetchPaystubs]);

  const recordDownload = useCallback(
    (entry: DownloadedFileItem) => {
      setDownloadedFiles((prev) => {
        // de-dupe by fileName + language + sequence
        const filtered = prev.filter(
          (f) =>
            !(
              f.sequenceNumber === entry.sequenceNumber &&
              f.language === entry.language &&
              f.fileName === entry.fileName
            )
        );
        const next = [entry, ...filtered];
        persistDownloads(next);
        return next;
      });
    },
    []
  );

  /** Print icon: fetch the PDF in the active language, open + download + record it. */
  const handlePrint = useCallback(
    async (item: PaystubItem, lang: LanguageTab = activeTab) => {
      const seq = String(
        item.sequenceNumber ?? item.SEQUENCENUMBER ?? item.id ?? ""
      );
      if (!seq || !empId) {
        toast.error("Cannot download: missing sequence or employee ID");
        return;
      }
      setDownloadingKey(`${lang}-${seq}`);
      try {
        const result =
          lang === "hindi"
            ? await fetchHindiPdf({
                empId,
                year: parseInt(selectedYear, 10),
                month: monthNumber,
                monthName: selectedMonthName,
                sequenceNumber: seq,
              })
            : await fetchEnglishPdf({ sequenceNumber: seq, empId });

        // open + download (web equivalent of mobile's "open file")
        openBase64Pdf(result.base64, result.fileName);
        downloadBase64Pdf(result.base64, result.fileName);

        const langSuffix = lang === "hindi" ? " (हिन्दी)" : "";
        recordDownload({
          id: `${seq}-${lang}-${Date.now()}`,
          fileName: result.fileName,
          label: `${getTranslatedType(item.type, lang === "hindi")} - ${item.paymentDate ?? ""}${langSuffix}`.trim(),
          downloadedAt: Date.now(),
          sequenceNumber: seq,
          language: lang,
          base64: result.base64,
        });
        toast.success(lang === "hindi" ? "डाउनलोड सफल" : "Downloaded");
      } catch (err: any) {
        toast.error(err?.message || (lang === "hindi" ? "डाउनलोड विफल" : "Download failed"));
      } finally {
        setDownloadingKey(null);
      }
    },
    [activeTab, empId, selectedYear, monthNumber, selectedMonthName, recordDownload]
  );

  /** PDF icon: open the "Downloaded statements" modal for this card. */
  const handleOpenDownloads = useCallback((item: PaystubItem) => {
    const seq = String(
      item.sequenceNumber ?? item.SEQUENCENUMBER ?? item.id ?? ""
    );
    setSelectedSeq(seq || null);
    setShowDownloadsModal(true);
  }, []);

  const removeDownload = useCallback((id: string) => {
    setDownloadedFiles((prev) => {
      const next = prev.filter((f) => f.id !== id);
      persistDownloads(next);
      return next;
    });
  }, []);

  const modalFiles = downloadedFiles.filter(
    (f) => !selectedSeq || f.sequenceNumber === selectedSeq
  );

  const renderPaystubCard = (item: PaystubItem, index: number, lang: LanguageTab = activeTab) => {
    const isHindi = lang === "hindi";
    const seq = String(
      item.sequenceNumber ?? item.SEQUENCENUMBER ?? item.id ?? index
    );
    const isDownloading = downloadingKey === `${lang}-${seq}`;
    const displayType = getTranslatedType(item.type, isHindi);

    return (
      <div className="col-12 col-xl-6" key={`${lang}-${seq}`}>
        <div className="salary-card h-100">
          <div className="salary-card-accent" />
          <div className="salary-card-body">
            <div className="d-flex align-items-start justify-content-between mb-3">
              <div className="d-flex align-items-center gap-3">
                <span className="salary-card-icon">
                  <i className="ti ti-receipt-2" />
                </span>
                <div>
                  <h6 className="mb-1 fw-bold text-dark">{displayType}</h6>
                  <span className="text-muted d-inline-flex align-items-center gap-1 fs-13">
                    <i className="ti ti-calendar-event" />
                    {item.paymentDate || "-"}
                  </span>
                </div>
              </div>
              <span className="badge badge-soft-success rounded-pill px-3 py-2">
                <i className="ti ti-circle-check me-1" />
                {isHindi ? "भुगतान हुआ" : "Paid"}
              </span>
            </div>

            <div className="row g-3 mb-3">
              <div className="col-6">
                <div className="salary-amount-box">
                  <span className="salary-amount-label">
                    <i className="ti ti-coin me-1" />
                    {isHindi ? "सकल राशि (Gross Amount)" : "Gross Amount"}
                  </span>
                  <span className="salary-amount-value">
                    {item.grossAmount || "-"}
                  </span>
                </div>
              </div>
              <div className="col-6">
                <div className="salary-amount-box salary-amount-box--net">
                  <span className="salary-amount-label">
                    <i className="ti ti-wallet me-1" />
                    {isHindi ? "शुद्ध भुगतान (Net Payment)" : "Net Payment"}
                  </span>
                  <span className="salary-amount-value text-primary">
                    {item.paymentAmount || "-"}
                  </span>
                </div>
              </div>
            </div>

            <div className="d-flex align-items-center justify-content-between pt-2 border-top">
              <span className="text-muted fs-12">
                {isHindi ? "संदर्भ: " : "Ref: "}
                <span className="fw-medium text-dark">{seq}</span>
              </span>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-primary d-inline-flex align-items-center gap-1"
                  onClick={() => handlePrint(item, lang)}
                  disabled={isDownloading}
                  title={isHindi ? "प्रिंट / डाउनलोड करें (हिन्दी)" : "Print / Download (English)"}
                >
                  {isDownloading ? (
                    <span className="spinner-border spinner-border-sm" role="status" />
                  ) : (
                    <i className="ti ti-printer" />
                  )}
                  <span>{isHindi ? "प्रिंट" : "Print"}</span>
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger d-inline-flex align-items-center gap-1"
                  onClick={() => handleOpenDownloads(item)}
                  title={isHindi ? "डाउनलोड किए गए विवरण देखें" : "View downloaded statements"}
                >
                  <i className="ti ti-file-type-pdf" />
                  <span>{isHindi ? "पीडीएफ" : "PDF"}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderPaystubList = (lang: LanguageTab = activeTab) => {
    const isHindi = lang === "hindi";
    if (loading) {
      return (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted mt-3 mb-0">
            {isHindi ? "वेतन विवरण लोड हो रहा है..." : "Loading salary statement..."}
          </p>
        </div>
      );
    }
    if (!paystubs.length) {
      const monthDisplay = isHindi
        ? HINDI_MONTH_LABELS[selectedMonthName] || selectedMonthName
        : selectedMonthName;
      return (
        <div className="salary-empty text-center py-5">
          <span className="salary-empty-icon">
            <i className="ti ti-calendar-off" />
          </span>
          <h6 className="mt-3 mb-1 text-dark">
            {isHindi ? "कोई वेतन भुगतान नहीं" : "No salary payments"}
          </h6>
          <p className="text-muted mb-0">
            {isHindi
              ? `${monthDisplay} ${selectedYear} के लिए कोई वेतन रिकॉर्ड नहीं है।`
              : `There are no salary records for ${selectedMonthName} ${selectedYear}.`}
          </p>
        </div>
      );
    }
    return (
      <div className="row g-3 pt-3">
        {paystubs.map((item, index) => renderPaystubCard(item, index, lang))}
      </div>
    );
  };

  const tabItems = [
    {
      key: "english",
      label: (
        <span className="d-inline-flex align-items-center gap-1">
          <i className="ti ti-language" /> English
        </span>
      ),
      children: renderPaystubList("english"),
    },
    {
      key: "hindi",
      label: (
        <span className="d-inline-flex align-items-center gap-1">
          <i className="ti ti-language-hiragana" /> Hindi
        </span>
      ),
      children: renderPaystubList("hindi"),
    },
  ];

  const isHindiActive = activeTab === "hindi";

  return (
    <div className="page-wrapper">
      <style>{salaryStyles}</style>
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <div className="my-auto mb-2">
                <h3 className="page-title mb-1">
                  {isHindiActive ? "वेतन विवरण (हिंदी) / Salary Statement (Hindi)" : "Salary Statement"}
                </h3>
                <nav>
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <Link to={all_routes.employeeDashboard}>Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to="#">Services</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      {isHindiActive ? "वेतन विवरण (Salary Statement)" : "Salary Statement"}
                    </li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
          <ul className="table-top-head">
            <li>
              <button
                type="button"
                className="btn btn-outline-primary d-inline-flex align-items-center"
                onClick={handleSync}
                disabled={syncing}
                title={isHindiActive ? "वेतन विवरण सिंक करें" : "Sync salary statements"}
              >
                {syncing ? (
                  <span className="spinner-border spinner-border-sm me-2" role="status" />
                ) : (
                  <i className="ti ti-refresh me-2" />
                )}
                {isHindiActive ? "सिंक (Sync)" : "Sync"}
              </button>
            </li>
          </ul>
        </div>

        {/* Filter bar */}
        <div className="card salary-filter-card mb-3">
          <div className="card-body py-3">
            <div className="row g-3 align-items-end">
              <div className="col-sm-6 col-md-4 col-lg-3">
                <label className="form-label fw-medium mb-1">
                  <i className="ti ti-calendar me-1 text-primary" />
                  {isHindiActive ? "वर्ष / Year" : "Year"}
                </label>
                <select
                  className="form-select"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                >
                  {YEAR_OPTIONS.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-sm-6 col-md-4 col-lg-3">
                <label className="form-label fw-medium mb-1">
                  <i className="ti ti-calendar-month me-1 text-primary" />
                  {isHindiActive ? "माह / Month" : "Month"}
                </label>
                <select
                  className="form-select"
                  value={selectedMonthName}
                  onChange={(e) => setSelectedMonthName(e.target.value)}
                >
                  {MONTH_OPTIONS.map((m) => (
                    <option key={m} value={m}>
                      {isHindiActive ? `${HINDI_MONTH_LABELS[m] || m} (${m})` : m}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-lg-6 d-none d-lg-flex justify-content-end">
                <div className="salary-period-pill">
                  <i className="ti ti-clock-hour-4 me-2" />
                  {isHindiActive ? (
                    <>
                      <span>विवरण प्रदर्शित:</span>
                      <span className="fw-semibold ms-1">
                        {HINDI_MONTH_LABELS[selectedMonthName] || selectedMonthName} {selectedYear}
                      </span>
                    </>
                  ) : (
                    <>
                      Showing statement for
                      <span className="fw-semibold ms-1">
                        {selectedMonthName} {selectedYear}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Statements */}
        <div className="card">
          <div className="card-body">
            <Tabs
              activeKey={activeTab}
              onChange={(key) => setActiveTab(key as LanguageTab)}
              items={tabItems}
              className="salary-tabs"
            />
          </div>
        </div>
      </div>

      {/* Downloaded statements modal */}
      {showDownloadsModal && (
        <>
          <div
            className="salary-modal-backdrop"
            onClick={() => setShowDownloadsModal(false)}
          />
          <div className="salary-modal" role="dialog" aria-modal="true">
            <div className="salary-modal-header">
              <h5 className="mb-0 text-primary fw-bold">
                {isHindiActive ? "डाउनलोड किए गए विवरण (Downloaded statements)" : "Downloaded statements"}
              </h5>
              <button
                type="button"
                className="btn-close-custom"
                onClick={() => setShowDownloadsModal(false)}
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <div className="salary-modal-body">
              {modalFiles.length === 0 ? (
                <div className="text-center py-4">
                  <span className="salary-empty-icon">
                    <i className="ti ti-folder-off" />
                  </span>
                  <p className="text-muted mt-3 mb-0">
                    {isHindiActive
                      ? "इस विवरण के लिए कोई डाउनलोड किया गया PDF नहीं है। पहले कार्ड से इसे डाउनलोड करें।"
                      : "No downloaded PDF for this statement. Download it first from the card."}
                  </p>
                </div>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {modalFiles.map((file) => (
                    <div className="salary-download-row" key={file.id}>
                      <span className="salary-pdf-badge">
                        <i className="ti ti-file-type-pdf" />
                      </span>
                      <div className="flex-grow-1 min-w-0">
                        <div className="fw-semibold text-dark text-truncate">
                          {file.label}
                        </div>
                        <div className="text-muted fs-12 text-truncate">
                          {file.fileName}
                        </div>
                        <span
                          className={`badge ${
                            file.language === "hindi"
                              ? "badge-soft-danger"
                              : "badge-soft-primary"
                          } mt-1`}
                        >
                          {file.language === "hindi" ? "हिन्दी" : "English"}
                        </span>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-light border"
                          title="Open PDF"
                          onClick={() =>
                            openBase64Pdf(file.base64, file.fileName)
                          }
                        >
                          <i className="ti ti-external-link" />
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-light border"
                          title="Download PDF"
                          onClick={() =>
                            downloadBase64Pdf(file.base64, file.fileName)
                          }
                        >
                          <i className="ti ti-download" />
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-light border text-danger"
                          title="Remove"
                          onClick={() => removeDownload(file.id)}
                        >
                          <i className="ti ti-trash" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const salaryStyles = `
.salary-filter-card { border: none; box-shadow: 0 2px 12px rgba(0,0,0,0.04); }
.salary-period-pill {
  display: inline-flex; align-items: center;
  background: rgba(var(--primary-rgb-picr, 220,38,38), 0.08);
  color: var(--primary, #dc2626);
  border-radius: 999px; padding: 8px 18px; font-size: 13px; font-weight: 500;
}
.salary-tabs .ant-tabs-tab { font-weight: 600; }
.salary-card {
  position: relative;
  background: #fff;
  border: 1px solid #eef0f4;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(16,24,40,0.04);
  transition: transform .18s ease, box-shadow .18s ease;
}
.salary-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 24px rgba(16,24,40,0.10);
}
.salary-card-accent {
  position: absolute; left: 0; top: 0; bottom: 0; width: 5px;
  background: linear-gradient(180deg, var(--primary, #dc2626), rgba(220,38,38,0.55));
}
.salary-card-body { padding: 18px 20px 16px 24px; }
.salary-card-icon {
  width: 46px; height: 46px; border-radius: 12px;
  display: inline-flex; align-items: center; justify-content: center;
  background: rgba(var(--primary-rgb-picr, 220,38,38), 0.10);
  color: var(--primary, #dc2626); font-size: 22px; flex-shrink: 0;
}
.salary-amount-box {
  background: #f8f9fc; border: 1px solid #eef0f4; border-radius: 10px;
  padding: 12px 14px; display: flex; flex-direction: column; height: 100%;
}
.salary-amount-box--net { background: rgba(var(--primary-rgb-picr, 220,38,38), 0.05); }
.salary-amount-label { font-size: 12px; color: #6b7280; font-weight: 500; margin-bottom: 4px; }
.salary-amount-value { font-size: 16px; font-weight: 700; color: #111827; }
.badge-soft-success { background: rgba(34,197,94,0.12); color: #16a34a; font-weight: 600; }
.badge-soft-primary { background: rgba(59,130,246,0.12); color: #2563eb; font-weight: 600; }
.badge-soft-danger { background: rgba(220,38,38,0.12); color: #dc2626; font-weight: 600; }
.fs-13 { font-size: 13px; }
.fs-12 { font-size: 12px; }
.min-w-0 { min-width: 0; }
.salary-empty-icon {
  width: 72px; height: 72px; border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  background: #f3f4f6; color: #9ca3af; font-size: 34px;
}
.salary-modal-backdrop {
  position: fixed; inset: 0; background: rgba(17,24,39,0.45);
  z-index: 1050; animation: salaryFade .18s ease;
}
.salary-modal {
  position: fixed; left: 50%; bottom: 0; transform: translateX(-50%);
  width: 100%; max-width: 560px; background: #fff;
  border-radius: 18px 18px 0 0; z-index: 1060;
  box-shadow: 0 -8px 40px rgba(0,0,0,0.18);
  animation: salarySlideUp .22s ease;
  max-height: 80vh; display: flex; flex-direction: column;
}
@media (min-width: 768px) {
  .salary-modal { bottom: auto; top: 50%; transform: translate(-50%, -50%); border-radius: 16px; }
}
.salary-modal-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 18px 20px; border-bottom: 1px solid #eef0f4;
}
.btn-close-custom {
  border: none; background: transparent; font-size: 22px; line-height: 1;
  color: #6b7280; cursor: pointer;
}
.salary-modal-body { padding: 16px 20px 22px; overflow-y: auto; }
.salary-download-row {
  display: flex; align-items: center; gap: 12px;
  border: 1px solid #eef0f4; border-radius: 12px; padding: 12px 14px;
  border-left: 4px solid var(--primary, #dc2626);
  background: #fff;
}
.salary-pdf-badge {
  width: 40px; height: 40px; border-radius: 10px; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  background: rgba(220,38,38,0.10); color: #dc2626; font-size: 20px;
}
@keyframes salarySlideUp { from { transform: translate(-50%, 100%); } to { transform: translate(-50%, 0); } }
@keyframes salaryFade { from { opacity: 0; } to { opacity: 1; } }
`;

export default SalaryStatement;
